import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateAge } from '../utils/ageCalculator';
import { usePresence } from '../hooks/usePresence';
import AvatarDisplay from './AvatarDisplay';

interface Student {
  uid: string;
  name: string;
  nickname: string;
  instrument: string;
  birthdate?: string;
  planStatus: 'active' | 'expired';
  avatarUrl?: string;
  practiceSessionDuration?: number;
  practiceSessionsPerWeek?: number;
  pendingGoalChanges?: {
    practiceSessionDuration?: number;
    practiceSessionsPerWeek?: number;
    requestedAt: any;
    status: 'pending' | 'approved' | 'rejected';
    reviewedBy?: string;
    reviewedAt?: any;
    reviewNotes?: string;
  };
}

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

interface WeeklyPracticeData {
  studentId: string;
  weeklyMinutes: number;
  averageSessionLength: number;
  practiceDays: boolean[]; // Array of 7 booleans for Sun-Sat
  dailyGoal: number;
  weeklyFrequency: number;
}

const TeacherDashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weeklyPracticeData, setWeeklyPracticeData] = useState<Record<string, WeeklyPracticeData>>({});
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Function to show feedback message with auto-hide
  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedbackMessage({ type, message });
    setTimeout(() => {
      setFeedbackMessage(null);
    }, 3000); // Auto-hide after 3 seconds
  };
  
  // Get student IDs for presence tracking
  const studentIds = students.map(student => student.uid);
  const { presence, loading: presenceLoading } = usePresence(studentIds);

  useEffect(() => {
    if (currentUser?.role !== 'teacher') {
      navigate('/dashboard');
      return;
    }
    fetchLinkedStudents();
  }, [currentUser, navigate]);

  // Load weekly data when students change
  useEffect(() => {
    if (students.length > 0) {
      loadWeeklyPracticeData(students);
    }
  }, [students]);

  // Weekly reset effect - refreshes data every Sunday at 12:01 AM
  useEffect(() => {
    const checkForWeeklyReset = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      // Check if it's Sunday and past 12:01 AM
      if (dayOfWeek === 0 && (hour > 0 || (hour === 0 && minute >= 1))) {
        // Refresh weekly data
        if (students.length > 0) {
          loadWeeklyPracticeData(students);
        }
      }
    };

    // Check immediately
    checkForWeeklyReset();

    // Set up interval to check every minute
    const interval = setInterval(checkForWeeklyReset, 60000); // 60000ms = 1 minute

    return () => clearInterval(interval);
  }, [students]);

  const fetchLinkedStudents = async () => {
    if (!currentUser?.linkedStudents || currentUser.linkedStudents.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const studentPromises = currentUser.linkedStudents.map(async (studentId) => {
        try {
          // Validate studentId before using it in document path
          if (!studentId || studentId.includes('/') || studentId.includes('//')) {
            console.warn(`Invalid student ID: ${studentId}`);
            return null;
          }
          
          const studentDoc = await getDoc(doc(db, 'users', studentId));
          if (studentDoc.exists()) {
            return {
              uid: studentId,
              ...studentDoc.data()
            } as Student;
          }
          return null;
        } catch (err) {
          console.warn(`Could not fetch student ${studentId}:`, err);
          // Return a placeholder for students we can't access
          return {
            uid: studentId,
            name: 'Unknown Student',
            nickname: 'Unknown',
            instrument: 'Unknown',
            birthdate: '',
            planStatus: 'expired' as const
          } as Student;
        }
      });

      const studentResults = await Promise.all(studentPromises);
      const validStudents = studentResults.filter((student): student is Student => student !== null);
      
      // Sort students alphabetically by name
      const sortedStudents = validStudents.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      
      setStudents(sortedStudents);
    } catch (err) {
      console.error('Error fetching linked students:', err);
      setError('Failed to load students. Some students may not be accessible.');
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyPracticeData = async (students: Student[]) => {
    const weeklyData: Record<string, WeeklyPracticeData> = {};

    for (const student of students) {
      try {
        // Calculate the start of the current week (Sunday)
        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - currentDay); // Go back to Sunday
        startOfWeek.setHours(0, 0, 0, 0); // Start of day

        // Calculate end of week (Saturday)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999); // End of day

        // Load practice sessions for this week
        // First, get all completed sessions for this student, then filter by date in code
        const sessionsQuery = query(
          collection(db, 'practiceSessions'),
          where('studentId', '==', student.uid),
          where('completed', '==', true)
        );

        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessions: PracticeSession[] = [];

        sessionsSnapshot.forEach((doc) => {
          const data = doc.data();
          const sessionEndDate = data.endAt?.toDate?.() || new Date(data.endAt || 0);
          
          // Filter sessions to only include this week's sessions
          if (sessionEndDate >= startOfWeek && sessionEndDate <= endOfWeek) {
            sessions.push({
              id: doc.id,
              studentId: data.studentId,
              lessonId: data.lessonId,
              plannedTotalMinutes: data.plannedTotalMinutes,
              plannedDurations: data.plannedDurations,
              actualDurations: data.actualDurations,
              completed: data.completed,
              startAt: data.startAt,
              endAt: data.endAt,
              durationSec: data.durationSec,
              taskCounts: data.taskCounts,
              createdAt: data.createdAt
            });
          }
        });

        // Calculate weekly statistics using all sessions (regardless of goal completion)
        const weeklyMinutes = sessions.reduce((total, session) => {
          return total + (session.durationSec || 0) / 60;
        }, 0);

        const averageSessionLength = sessions.length > 0 
          ? weeklyMinutes / sessions.length 
          : 0;

        // Create practice days array (Sunday = index 0, Saturday = index 6)
        const practiceDays = [false, false, false, false, false, false, false];
        
        sessions.forEach(session => {
          const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
          const dayOfWeek = sessionDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
          practiceDays[dayOfWeek] = true;
        });

        weeklyData[student.uid] = {
          studentId: student.uid,
          weeklyMinutes,
          averageSessionLength,
          practiceDays,
          dailyGoal: student.practiceSessionDuration || 30,
          weeklyFrequency: student.practiceSessionsPerWeek || 5
        };
      } catch (error) {
        console.error(`Error loading weekly data for student ${student.uid}:`, error);
        // Set default values for this student
        weeklyData[student.uid] = {
          studentId: student.uid,
          weeklyMinutes: 0,
          averageSessionLength: 0,
          practiceDays: [false, false, false, false, false, false, false],
          dailyGoal: student.practiceSessionDuration || 30,
          weeklyFrequency: student.practiceSessionsPerWeek || 5
        };
      }
    }

    setWeeklyPracticeData(weeklyData);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleViewSummaries = (studentId: string) => {
    navigate(`/teacher/summaries/${studentId}`);
  };

  const handleViewTasks = (studentId: string) => {
    navigate(`/practice-tasks?studentId=${studentId}`);
  };

  const handleRecordLesson = (studentId: string) => {
    navigate(`/teacher/record/${studentId}`);
  };

  const handleViewAwards = (studentId: string) => {
    navigate(`/teacher/awards/${studentId}`);
  };

  const handleViewAnalytics = (studentId: string) => {
    navigate(`/teacher/analytics/${studentId}`);
  };

  // Handle goal change requests
  const handleGoalChangeRequest = async (studentId: string, action: 'approve' | 'reject', reviewNotes?: string) => {
    try {
      const student = students.find(s => s.uid === studentId);
      if (!student?.pendingGoalChanges) return;

      const updateData: any = {
        pendingGoalChanges: {
          ...student.pendingGoalChanges,
          status: action,
          reviewedBy: currentUser!.uid,
          reviewedAt: new Date(),
          reviewNotes: reviewNotes || ''
        }
      };

      // If approved, update the actual goal values
      if (action === 'approve') {
        if (student.pendingGoalChanges.practiceSessionDuration !== undefined) {
          updateData.practiceSessionDuration = student.pendingGoalChanges.practiceSessionDuration;
        }
        if (student.pendingGoalChanges.practiceSessionsPerWeek !== undefined) {
          updateData.practiceSessionsPerWeek = student.pendingGoalChanges.practiceSessionsPerWeek;
        }
      }

      await updateDoc(doc(db, 'users', studentId), updateData);

      // Refresh students data
      await fetchLinkedStudents();
      
      showFeedback('success', `Goal change request ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing goal change request:`, error);
      showFeedback('error', `Failed to ${action} goal change request. Please try again.`);
    }
  };

  const handleRemoveStudent = async (studentId: string, studentName: string) => {
    const confirmMessage = `Are you sure you want to remove ${studentName} from your linked students?\n\nThis will:\n• Remove the student from your dashboard\n• Remove you from the student's linked teachers\n• The student will no longer be able to access your lessons\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      
      // First, verify the student is actually linked
      if (!currentUser?.linkedStudents?.includes(studentId)) {
        alert('This student is not currently linked to your account.');
        return;
      }
      
      // Remove student from teacher's linkedStudents array
      await updateDoc(doc(db, 'users', currentUser!.uid), {
        linkedStudents: arrayRemove(studentId)
      });

      // Remove teacher from student's linkedTeachers array
      await updateDoc(doc(db, 'users', studentId), {
        linkedTeachers: arrayRemove(currentUser!.uid)
      });

      // Update local state to remove the student from the list
      setStudents(prevStudents => prevStudents.filter(student => student.uid !== studentId));
      
      // Show success message
      showFeedback('success', `Successfully removed ${studentName} from your linked students.`);
      
    } catch (error: any) {
      console.error('Error removing student:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to remove student. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. You may not have permission to modify this student\'s data.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Student not found. They may have already been removed.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Operation failed due to a precondition. The student may have already been unlinked.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data provided. Please refresh the page and try again.';
      }
      
      showFeedback('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else {
      return `${mins}m`;
    }
  };

  const getDayInitials = (): string[] => {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // Sunday to Saturday
  };

  const WeeklyPracticeTracker: React.FC<{ studentId: string }> = ({ studentId }) => {
    const data = weeklyPracticeData[studentId];
    const student = students.find(s => s.uid === studentId);
    const [isEditingGoals, setIsEditingGoals] = useState(false);
    const [editingDailyGoal, setEditingDailyGoal] = useState(student?.practiceSessionDuration || 30);
    const [editingWeeklyGoal, setEditingWeeklyGoal] = useState(student?.practiceSessionsPerWeek || 3);
    
    if (!data) {
      return (
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500">Loading practice data...</div>
        </div>
      );
    }

    // Check if we have valid practice days data
    if (!data.practiceDays || data.practiceDays.length !== 7) {
      return (
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500">No practice data available</div>
        </div>
      );
    }

    const dayInitials = getDayInitials();

    // Handle editing student goals
    const handleEditGoals = () => {
      setEditingDailyGoal(student?.practiceSessionDuration || 30);
      setEditingWeeklyGoal(student?.practiceSessionsPerWeek || 3);
      setIsEditingGoals(true);
    };

    const handleSaveGoals = async () => {
      if (!student) return;
      
      try {
        await updateDoc(doc(db, 'users', student.uid), {
          practiceSessionDuration: editingDailyGoal,
          practiceSessionsPerWeek: editingWeeklyGoal,
          // Clear any pending goal changes when teacher directly edits
          pendingGoalChanges: null
        });
        
        // Refresh students data
        await fetchLinkedStudents();
        setIsEditingGoals(false);
        showFeedback('success', 'Student goals updated successfully!');
      } catch (error) {
        console.error('Error updating student goals:', error);
        showFeedback('error', 'Failed to update student goals. Please try again.');
      }
    };

    const handleCancelEdit = () => {
      setIsEditingGoals(false);
      setEditingDailyGoal(student?.practiceSessionDuration || 30);
      setEditingWeeklyGoal(student?.practiceSessionsPerWeek || 3);
    };

    // Check if student has pending goal changes
    const hasPendingGoals = student?.pendingGoalChanges?.status === 'pending';

    return (
      <div className="flex items-center space-x-4">
        {/* Weekly Practice Circles */}
        <div className="flex items-center space-x-1">
          {dayInitials.map((day, index) => {
            const practiced = data.practiceDays[index];
            return (
              <div
                key={index}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                  practiced
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-500'
                }`}
                title={`${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]}${practiced ? ' - Practiced' : ' - No practice'}`}
              >
                {day}
              </div>
            );
          })}
        </div>

        {/* Practice Statistics */}
        <div className="space-y-3">
          {/* Edit Button */}
          {!isEditingGoals && (
            <div className="flex justify-center">
              <button
                onClick={handleEditGoals}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md font-medium transition-colors duration-200"
              >
                Edit Goals
              </button>
            </div>
          )}
          
          <div className="flex items-center space-x-4 text-sm">
            {/* Daily Goal */}
            <div className="text-center">
              {isEditingGoals ? (
                <div className="space-y-1">
                  <input
                    type="number"
                    value={editingDailyGoal}
                    onChange={(e) => setEditingDailyGoal(parseInt(e.target.value) || 30)}
                    min="1"
                    max="180"
                    className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="text-xs text-gray-500">Daily Goal (min)</div>
                </div>
              ) : (
                <>
                  <div className="font-semibold text-gray-900">{data.dailyGoal}m</div>
                  <div className="text-xs text-gray-500">Daily Goal</div>
                </>
              )}
            </div>
            
            {/* Weekly Frequency */}
            <div className="text-center">
              {isEditingGoals ? (
                <div className="space-y-1">
                  <input
                    type="number"
                    value={editingWeeklyGoal}
                    onChange={(e) => setEditingWeeklyGoal(parseInt(e.target.value) || 3)}
                    min="1"
                    max="7"
                    className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="text-xs text-gray-500">Per Week</div>
                </div>
              ) : (
                <>
                  <div className="font-semibold text-gray-900">{data.weeklyFrequency}</div>
                  <div className="text-xs text-gray-500">Per Week</div>
                </>
              )}
            </div>
            
            {/* Average Length - not editable */}
            <div className="text-center">
              <div className="font-semibold text-gray-900">{formatMinutes(data.averageSessionLength)}</div>
              <div className="text-xs text-gray-500">Avg Length</div>
            </div>
            
            {/* This Week - not editable */}
            <div className="text-center">
              <div className="font-semibold text-gray-900">{formatMinutes(data.weeklyMinutes)}</div>
              <div className="text-xs text-gray-500">This Week</div>
            </div>
          </div>
          
          {/* Goal Request Details */}
          {hasPendingGoals && student?.pendingGoalChanges && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="text-xs text-yellow-800">
                <div className="font-medium mb-1">Goal Change Request:</div>
                {student.pendingGoalChanges.practiceSessionDuration && (
                  <div className="mb-1">
                    Daily Goal: {student.practiceSessionDuration} → {student.pendingGoalChanges.practiceSessionDuration} minutes
                  </div>
                )}
                {student.pendingGoalChanges.practiceSessionsPerWeek && (
                  <div className="mb-1">
                    Weekly Frequency: {student.practiceSessionsPerWeek} → {student.pendingGoalChanges.practiceSessionsPerWeek} sessions/week
                  </div>
                )}
                <div className="text-yellow-600">
                  Requested: {student.pendingGoalChanges.requestedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                </div>
              </div>
            </div>
          )}

          {/* Approve Button */}
          {!isEditingGoals && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => handleGoalChangeRequest(studentId, 'approve')}
                className={`text-xs px-3 py-1 rounded-md font-medium transition-colors duration-200 ${
                  hasPendingGoals 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                }`}
                disabled={!hasPendingGoals}
                title={hasPendingGoals ? 'Approve pending goal request' : 'No pending goal requests'}
              >
                {hasPendingGoals ? 'Approve Request' : 'No Pending Requests'}
              </button>
            </div>
          )}
          
          {/* Save/Cancel Buttons when editing */}
          {isEditingGoals && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={handleSaveGoals}
                className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md font-medium transition-colors duration-200"
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-md font-medium transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">👨‍🏫</div>
                <h1 className="text-xl font-bold text-white">
                  Teacher Dashboard
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white/90 font-medium">
                Welcome, {currentUser?.nickname} (Teacher)
              </span>
              <button
                onClick={() => navigate('/profile/edit')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Edit Profile
              </button>
              <button
                onClick={handleLogout}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        
        {/* Feedback Message */}
        {feedbackMessage && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
            feedbackMessage.type === 'success' 
              ? 'bg-green-100 border border-green-200 text-green-800' 
              : 'bg-red-100 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0">
                {feedbackMessage.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="text-sm font-medium">
                {feedbackMessage.message}
              </div>
              <button
                onClick={() => setFeedbackMessage(null)}
                className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Your Students
              </h2>
              <p className="text-gray-600">
                {students.length} student{students.length !== 1 ? 's' : ''} linked
              </p>
            </div>
            {students.length > 0 && (
              <button
                onClick={() => navigate('/teacher/scan')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add Student</span>
              </button>
            )}
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {students.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-xl p-12 text-center border border-white/20">
              <div className="text-indigo-400 mb-6">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No students linked yet</h3>
              <p className="text-gray-600 mb-8 text-lg">
                Students need to share their QR code with you to get started.
              </p>
              <button
                onClick={() => navigate('/teacher/scan')}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Scan Student QR Code
              </button>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm shadow-xl overflow-hidden sm:rounded-xl border border-white/20">
              <ul className="divide-y divide-gray-200/50">
                {students.map((student) => (
                  <li key={student.uid} className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 transition-all duration-200">
                    <div className="px-6 py-5 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <AvatarDisplay 
                            user={student}
                            size="lg"
                            className="ring-2 ring-white shadow-lg"
                            alt={`${student.name}'s avatar`}
                          />
                        </div>
                        <div className="ml-5 flex-1">
                          <div className="flex items-center">
                            <p className="text-lg font-semibold text-gray-900">
                              {student.name} ({student.nickname})
                            </p>
                            {/* Online/Offline Status Indicator */}
                            <div className="ml-3 flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-2 ${
                                presence[student.uid]?.online 
                                  ? 'bg-green-500 animate-pulse' 
                                  : 'bg-red-500'
                              }`} title={
                                presence[student.uid]?.online 
                                  ? 'Online' 
                                  : 'Offline'
                              }></div>
                              <span className="text-xs text-gray-500">
                                {presence[student.uid]?.online ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            <span className={`ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              student.planStatus === 'active' 
                                ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200' 
                                : 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200'
                            }`}>
                              {student.planStatus}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-medium mb-3">
                            {student.instrument} • Age {student.birthdate ? calculateAge(student.birthdate) : 'Not set'}
                          </p>
                          
                          {/* Weekly Practice Tracker */}
                          <WeeklyPracticeTracker studentId={student.uid} />
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewSummaries(student.uid)}
                          className="text-indigo-600 hover:text-indigo-900 p-3 rounded-lg hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="View Lesson Summaries"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleViewTasks(student.uid)}
                          className="text-purple-600 hover:text-purple-900 p-3 rounded-lg hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="View Practice Tasks"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleViewAwards(student.uid)}
                          className="text-yellow-600 hover:text-yellow-900 p-3 rounded-lg hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="Assign Awards & Badges"
                        >
                          <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleViewAnalytics(student.uid)}
                          className="text-indigo-600 hover:text-indigo-900 p-3 rounded-lg hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="View Student Analytics"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRecordLesson(student.uid)}
                          disabled={student.planStatus !== 'active'}
                          className={`p-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
                            student.planStatus === 'active'
                              ? 'text-green-600 hover:text-green-900 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50'
                              : 'text-gray-400 cursor-not-allowed'
                          }`}
                          title={student.planStatus === 'active' ? 'Record Lesson' : 'Student subscription required'}
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveStudent(student.uid, student.name)}
                          className="text-red-600 hover:text-red-900 p-3 rounded-lg hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="Remove Linked Student"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Goal Change Requests Section */}
          {students.some(s => s.pendingGoalChanges?.status === 'pending') && (
            <div className="mt-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending Goal Requests</h3>
                <p className="text-sm text-gray-600">
                  {students.filter(s => s.pendingGoalChanges?.status === 'pending').some(s => s.practiceSessionDuration === 5 && s.practiceSessionsPerWeek === 30) 
                    ? 'Students have requested initial goal setup or changes to their practice goals' 
                    : 'Students have requested changes to their practice goals'
                  }
                </p>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm shadow-xl overflow-hidden sm:rounded-xl border border-white/20">
                <ul className="divide-y divide-gray-200/50">
                  {students
                    .filter(s => s.pendingGoalChanges?.status === 'pending')
                    .map((student) => (
                    <li key={`goal-request-${student.uid}`} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <AvatarDisplay 
                            user={student}
                            size="md"
                            alt={`${student.name}'s avatar`}
                          />
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{student.name}</h4>
                            <div className="text-sm text-gray-500">
                              {student.pendingGoalChanges?.practiceSessionDuration && (
                                <span className="block">
                                  Daily Goal: {student.practiceSessionDuration} → {student.pendingGoalChanges.practiceSessionDuration} minutes
                                  {student.practiceSessionDuration === 5 && ' (Initial Setup)'}
                                </span>
                              )}
                              {student.pendingGoalChanges?.practiceSessionsPerWeek && (
                                <span className="block">
                                  Weekly Frequency: {student.practiceSessionsPerWeek} → {student.pendingGoalChanges.practiceSessionsPerWeek} sessions/week
                                  {student.practiceSessionsPerWeek === 30 && ' (Initial Setup)'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              Requested: {student.pendingGoalChanges?.requestedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                              {student.practiceSessionDuration === 5 && student.practiceSessionsPerWeek === 30 && (
                                <span className="ml-2 text-yellow-600 font-medium">(New Student Setup)</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const notes = prompt('Add approval notes (optional):');
                              handleGoalChangeRequest(student.uid, 'approve', notes || undefined);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const notes = prompt('Add rejection notes (optional):');
                              handleGoalChangeRequest(student.uid, 'reject', notes || undefined);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeacherDashboard;
